"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import posthog from "posthog-js";
import { BaseErrorPage } from "@/components/ui/error-layout";
import { isNextNavigationError } from "@/lib/auth/next-navigation-errors";

/**
 * Root error boundary.
 *
 * IMPORTANT: `redirect()` and `notFound()` from `next/navigation` throw
 * sentinel errors (digests `NEXT_REDIRECT` / `NEXT_NOT_FOUND`) that Next's
 * framework is expected to catch. These can bubble up here during the
 * logout → back-button flow when the server-rendered authenticated layout
 * runs `redirect("/login")` mid-transition. If we render a fallback UI,
 * the user sees an "UNCAUGHT_EXCEPTION: NEXT_REDIRECT" overlay.
 *
 * We re-throw those sentinels so Next can process them (perform the
 * redirect / 404) instead of surfacing them as user-facing errors.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  const isSentinel = isNextNavigationError(error);

  useEffect(() => {
    // Re-throw framework sentinels so Next can handle the navigation.
    if (isSentinel) {
      throw error;
    }

    // Capture real exceptions to PostHog
    if (typeof window !== "undefined" && posthog) {
      posthog.captureException(error, {
        $exception_type: error.name,
        $exception_message: error.message,
        $exception_stack: error.stack,
        error_id: error.digest,
        error_tier: "contextual",
        context: "root_error_boundary",
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    }

    console.error("[root_error_boundary] Error:", error);
  }, [error, isSentinel]);

  if (isSentinel) {
    // Render nothing while the effect re-throws; avoids flash of error UI.
    return null;
  }

  return (
    <BaseErrorPage
      tier="contextual"
      title="Something Went Wrong"
      description="We encountered an unexpected error while processing your request. This has been logged and our team has been notified."
      error={error}
      onRetry={reset}
      onGoHome={() => router.push("/home")}
      trackingContext="root_error"
    />
  );
}
